import { BookmarkCheckIcon, BookmarkIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { useAuth } from "@/utils/auth";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { graphql, useFragment, useMutation } from "react-relay";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { PaperBookmarkButtonBookmarkMutation } from "./__generated__/PaperBookmarkButtonBookmarkMutation.graphql";
import { PaperBookmarkButtonFragment$key } from "./__generated__/PaperBookmarkButtonFragment.graphql";
import { PaperBookmarkButtonUnbookmarkMutation } from "./__generated__/PaperBookmarkButtonUnbookmarkMutation.graphql";

const Fragment = graphql`
  fragment PaperBookmarkButtonFragment on Paper {
    id
    bookmarkCount
    viewerBookmarked
  }
`;

const BookmarkMutation = graphql`
  mutation PaperBookmarkButtonBookmarkMutation($id: ID!) {
    bookmarkPaper(id: $id) {
      ...PaperBookmarkButtonFragment
    }
  }
`;

const UnbookmarkMutation = graphql`
  mutation PaperBookmarkButtonUnbookmarkMutation($id: ID!) {
    unbookmarkPaper(id: $id) {
      ...PaperBookmarkButtonFragment
    }
  }
`;

/** Moves the viewer's bookmark count, shown on the papers page, along with
 * the bookmark. */
function countBookmark(change: number) {
  return (store: RecordSourceSelectorProxy) => {
    const viewer = store.getRoot().getLinkedRecord("viewer");
    const count = viewer?.getValue("bookmarkedPaperCount");
    if (viewer && typeof count === "number") {
      viewer.setValue(count + change, "bookmarkedPaperCount");
    }
  };
}

export interface PaperBookmarkButtonProps {
  paper: PaperBookmarkButtonFragment$key;
  /** Show a label next to the icon instead of the bookmark count. */
  labelled?: boolean;
}

/** Bookmarks for signed-in users; everyone else sees the count only. */
export default function PaperBookmarkButton(props: PaperBookmarkButtonProps) {
  const intl = useIntl();
  const { isAuthenticated } = useAuth();
  const paper = useFragment(Fragment, props.paper);
  const [bookmark, bookmarking] =
    useMutation<PaperBookmarkButtonBookmarkMutation>(BookmarkMutation);
  const [unbookmark, unbookmarking] =
    useMutation<PaperBookmarkButtonUnbookmarkMutation>(UnbookmarkMutation);
  const bookmarked = !!paper.viewerBookmarked;
  const Icon = bookmarked ? BookmarkCheckIcon : BookmarkIcon;

  if (!isAuthenticated || paper.viewerBookmarked == null) {
    return paper.bookmarkCount > 0 ? (
      <span className="inline-flex items-center gap-1 text-grey-500 text-sm">
        <BookmarkIcon className="w-4 h-4" />
        <FormattedNumber value={paper.bookmarkCount} notation="compact" />
      </span>
    ) : null;
  }

  return (
    <Button
      type="button"
      variant="tertiary"
      hue="grey"
      size="sm"
      className="gap-1.5"
      aria-pressed={bookmarked}
      disabled={bookmarking || unbookmarking}
      title={
        bookmarked
          ? intl.formatMessage({ defaultMessage: "Remove bookmark" })
          : intl.formatMessage({ defaultMessage: "Bookmark" })
      }
      onClick={(event) => {
        // The button can sit inside a link to the paper.
        event.preventDefault();
        event.stopPropagation();
        const optimistic = {
          id: paper.id,
          bookmarkCount: paper.bookmarkCount + (bookmarked ? -1 : 1),
          viewerBookmarked: !bookmarked,
        };
        const variables = { id: paper.id };
        // The optimistic update is rolled back before the server's lands, so
        // both count the change.
        const updater = countBookmark(bookmarked ? -1 : 1);
        if (bookmarked) {
          unbookmark({
            variables,
            optimisticResponse: { unbookmarkPaper: optimistic },
            optimisticUpdater: updater,
            updater,
          });
        } else {
          bookmark({
            variables,
            optimisticResponse: { bookmarkPaper: optimistic },
            optimisticUpdater: updater,
            updater,
          });
        }
      }}
    >
      <Icon className={bookmarked ? "w-5 h-5 text-brand-500" : "w-5 h-5"} />
      {props.labelled ? (
        bookmarked ? (
          <FormattedMessage defaultMessage="Bookmarked" />
        ) : (
          <FormattedMessage defaultMessage="Bookmark" />
        )
      ) : (
        paper.bookmarkCount > 0 && (
          <FormattedNumber value={paper.bookmarkCount} notation="compact" />
        )
      )}
    </Button>
  );
}
